import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/store/root-state';
import { logoutUser } from '@/features/auth/authThunks';
import { BootstrapPhase, type AppState } from './appStateTypes';

const initialState: AppState = {
  bootstrapPhase: BootstrapPhase.Idle,
  bootstrapMessage: null,
  bootstrapError: null,
  isBootstrapping: false,
};

type SetBootstrapPhasePayload = {
  phase: BootstrapPhase;
  message?: string | null;
};

export const appStateSlice = createSlice({
  name: 'appState',
  initialState,
  reducers: {
    setBootstrapPhase: (state, action: PayloadAction<SetBootstrapPhasePayload>) => {
      state.bootstrapPhase = action.payload.phase;
      state.bootstrapMessage = action.payload.message ?? null;

      if (action.payload.phase !== BootstrapPhase.Error) {
        state.bootstrapError = null;
      }
    },
    setBootstrapError: (state, action: PayloadAction<string>) => {
      state.bootstrapPhase = BootstrapPhase.Error;
      state.bootstrapError = action.payload;
      state.bootstrapMessage = 'Bootstrap failed.';
    },
    setBootstrapping: (state, action: PayloadAction<boolean>) => {
      state.isBootstrapping = action.payload;
    },
    resetAppState: () => ({ ...initialState }),
  },
  extraReducers: (builder) => {
    builder.addCase(logoutUser.fulfilled, (state) => {
      state.bootstrapPhase = BootstrapPhase.Unauthenticated;
      state.bootstrapMessage = null;
      state.bootstrapError = null;
      state.isBootstrapping = false;
    });
  },
});

export const { setBootstrapPhase, setBootstrapError, setBootstrapping, resetAppState } = appStateSlice.actions;

export default appStateSlice.reducer;

const selectAppState = (state: RootState) => state.appState;

export const selectBootstrapPhase = createSelector(selectAppState, (s) => s.bootstrapPhase);
export const selectBootstrapMessage = createSelector(selectAppState, (s) => s.bootstrapMessage);
export const selectBootstrapError = createSelector(selectAppState, (s) => s.bootstrapError);
export const selectIsBootstrapping = createSelector(selectAppState, (s) => s.isBootstrapping);
